/*
 * Keeping the phone off a stale build.
 *
 * On iOS the app is almost never navigated. It is opened from the home
 * screen once and then resumed from the app switcher for days, so the
 * service worker's network-first navigation never fires and the page that
 * was loaded on Tuesday is still the page running on Friday.
 *
 * So we ask, on every resume: is the build I am running still the build
 * that is deployed? Vite stamps a content hash into the entry script's
 * filename, which makes that filename the build's identity. If the
 * deployed index.html points at a different one, there is a newer build
 * and the page reloads itself onto it.
 */

/* Resuming twice in a minute is one resume. The probe is tiny, but it is
   not free on a phone connection. */
const THROTTLE = 60_000

let lastCheck = 0
let reloading = false

function runningEntry() {
  const el = document.querySelector('script[type="module"][src]')
  return el ? new URL(el.src, location.href).pathname : null
}

async function deployedEntry() {
  const res = await fetch('/index.html', { cache: 'no-store' })
  if (!res.ok) return null
  /* Parsed rather than pattern-matched: attribute order in the built file is
     Vite's to change, and DOMParser does not run anything it reads. */
  const doc = new DOMParser().parseFromString(await res.text(), 'text/html')
  const el = doc.querySelector('script[type="module"][src]')
  return el ? new URL(el.getAttribute('src'), location.href).pathname : null
}

/* Never yank the page out from under an open editor. A deploy can wait
   until he has closed the sheet. */
function busy() {
  if (document.querySelector('.sheet-backdrop')) return true
  const el = document.activeElement
  if (!el) return false
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable
}

async function check() {
  if (reloading) return
  if (Date.now() - lastCheck < THROTTLE) return
  lastCheck = Date.now()

  if (busy()) {
    /* Try again on the next resume rather than sitting out the throttle. */
    lastCheck = 0
    return
  }

  /* Offline, or the probe failed: nothing to do. Running yesterday's build
     is a great deal better than throwing an error at him. */
  let deployed = null
  try {
    deployed = await deployedEntry()
  } catch {
    return
  }

  const running = runningEntry()
  if (!deployed || !running || deployed === running) return
  if (busy()) return

  reloading = true
  location.reload()
}

export function watchForUpdates() {
  /* In dev the entry is /src/main.jsx with no hash, so there is nothing to
     compare and nothing to reload for. */
  if (import.meta.env.DEV) return

  lastCheck = Date.now()

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') check()
  })

  /* Safari restores the whole page from its back/forward cache on resume,
     which fires no visibilitychange of its own. */
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) check()
  })
}
